import { defineStore } from 'pinia'
import { ref, watch } from 'vue'

const STORAGE_KEY = 'nerdcrm-ui-preferences'

export type DensityMode = 'normal' | 'comfortable' | 'compact'

export const TIMEZONES = [
  { value: 'UTC', label: 'UTC' },
  { value: 'America/New_York', label: 'Eastern Time (America/New_York)' },
  { value: 'America/Chicago', label: 'Central Time (America/Chicago)' },
  { value: 'America/Denver', label: 'Mountain Time (America/Denver)' },
  { value: 'America/Los_Angeles', label: 'Pacific Time (America/Los_Angeles)' },
  { value: 'Europe/London', label: 'London (Europe/London)' },
  { value: 'Europe/Paris', label: 'Paris (Europe/Paris)' },
  { value: 'Europe/Berlin', label: 'Berlin (Europe/Berlin)' },
  { value: 'Asia/Dhaka', label: 'Bangladesh (Asia/Dhaka)' },
  { value: 'Asia/Dubai', label: 'Dubai (Asia/Dubai)' },
  { value: 'Asia/Kolkata', label: 'India (Asia/Kolkata)' },
  { value: 'Asia/Shanghai', label: 'Shanghai (Asia/Shanghai)' },
  { value: 'Asia/Singapore', label: 'Singapore (Asia/Singapore)' },
  { value: 'Asia/Tokyo', label: 'Tokyo (Asia/Tokyo)' },
  { value: 'Australia/Sydney', label: 'Sydney (Australia/Sydney)' },
]

interface UIPreferences {
  densityMode: DensityMode
  sidebarCollapsed: boolean
  timezone: string
}

const defaultPreferences: UIPreferences = {
  densityMode: 'normal',
  sidebarCollapsed: false,
  timezone: 'UTC',
}

export const useUIStore = defineStore('ui', () => {
  // State
  const densityMode = ref<DensityMode>('normal')
  const sidebarCollapsed = ref(false)
  const timezone = ref('UTC')

  // Load preferences from localStorage
  function loadPreferences() {
    try {
      const stored = localStorage.getItem(STORAGE_KEY)
      if (stored) {
        const preferences = JSON.parse(stored)
        // Handle legacy compactMode boolean
        if (typeof preferences.compactMode === 'boolean') {
          densityMode.value = preferences.compactMode ? 'compact' : 'normal'
        } else {
          densityMode.value = preferences.densityMode ?? defaultPreferences.densityMode
        }
        sidebarCollapsed.value = preferences.sidebarCollapsed ?? defaultPreferences.sidebarCollapsed
        timezone.value = preferences.timezone ?? defaultPreferences.timezone
      }
    } catch (error) {
      console.error('Failed to load UI preferences:', error)
    }

    // Apply density mode class to document
    applyDensityMode()
  }

  // Save preferences to localStorage
  function savePreferences() {
    try {
      const preferences: UIPreferences = {
        densityMode: densityMode.value,
        sidebarCollapsed: sidebarCollapsed.value,
        timezone: timezone.value,
      }
      localStorage.setItem(STORAGE_KEY, JSON.stringify(preferences))
    } catch (error) {
      console.error('Failed to save UI preferences:', error)
    }
  }

  // Apply density mode class to document root
  function applyDensityMode() {
    // Remove all density classes first
    document.documentElement.classList.remove('comfortable', 'compact')

    // Add the appropriate class
    if (densityMode.value === 'comfortable') {
      document.documentElement.classList.add('comfortable')
    } else if (densityMode.value === 'compact') {
      document.documentElement.classList.add('compact')
    }
  }

  // Set density mode
  function setDensityMode(mode: DensityMode) {
    densityMode.value = mode
    applyDensityMode()
    savePreferences()
  }

  // Toggle sidebar collapsed
  function toggleSidebar() {
    sidebarCollapsed.value = !sidebarCollapsed.value
    savePreferences()
  }

  // Set timezone
  function setTimezone(tz: string) {
    timezone.value = tz
    savePreferences()
  }

  // Watch for changes and save
  watch([densityMode, sidebarCollapsed, timezone], () => {
    savePreferences()
  })

  return {
    // State
    densityMode,
    sidebarCollapsed,
    timezone,

    // Actions
    loadPreferences,
    setDensityMode,
    toggleSidebar,
    setTimezone,
  }
})
